import { useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { flattenPages, getNextPageOffset } from '@core/hooks/useInfinitePage';
import { useInfiniteScrollSentinel } from '@core/hooks/useInfiniteScrollSentinel';
import { type DesignQuery, designKeys, getDesignsPage } from '../../api/design';

const PAGE_SIZE = 40;

/**
 * Designs matching `query`, loaded a page at a time as the returned
 * `sentinelRef` (placed after the list) scrolls into view.
 */
export function useDesignPages(query?: DesignQuery) {
  const {
    data,
    isPending,
    isError,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  } = useInfiniteQuery({
    queryKey: designKeys.page(query),
    queryFn: ({ pageParam }) =>
      getDesignsPage({ ...query, offset: pageParam, limit: PAGE_SIZE }),
    initialPageParam: 0,
    getNextPageParam: getNextPageOffset,
  });

  const designs = useMemo(() => flattenPages(data?.pages), [data]);

  const sentinelRef = useInfiniteScrollSentinel({
    hasMore: Boolean(hasNextPage) && !isFetchNextPageError,
    loading: isFetchingNextPage,
    onLoadMore: fetchNextPage,
  });

  return {
    designs,
    isPending,
    isError,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    sentinelRef,
  };
}

export type DesignPages = ReturnType<typeof useDesignPages>;
